import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PricingSection from '../home/components/PricingSection';
import HowItWorksSection from '../home/components/HowItWorksSection';
import { useLanguage } from '../../contexts/LanguageContext';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://automatchlab.com';

const servicesSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/services#webpage`,
      url: `${siteUrl}/services`,
      name: 'Automatchlab Services - Expert Vehicle Consulting & Matchmaking',
      isPartOf: { '@id': `${siteUrl}/#website` },
      description:
        'Explore Automatchlab\'s expert vehicle consulting services. From discovery to delivery, we match you with the right vehicle using AI-powered intelligence and human expertise.',
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service`,
      name: 'Expert Vehicle Consulting & Matchmaking',
      provider: {
        '@type': 'Organization',
        name: 'Automatchlab',
        url: `${siteUrl}/`,
      },
      description:
        'Comprehensive vehicle matchmaking service covering all makes, models, and price points. AI-powered analysis combined with expert human guidance ensures you find the perfect vehicle for your needs.',
      url: `${siteUrl}/services`,
      serviceType: 'Vehicle Consulting',
      areaServed: 'Canada',
      offers: {
        '@type': 'Offer',
        description: 'One-time vehicle intelligence consultation and matchmaking service.',
      },
    },
  ],
};

export default function ServicesPage() {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const tx = {
    en: {
      back: 'Back',
      tagline: 'Our Services',
      subtitle: 'Expert vehicle consulting, from discovery to delivery.',
    },
    fr: {
      back: 'Retour',
      tagline: 'Nos Services',
      subtitle: 'Conseil automobile expert, de la découverte à la livraison.',
    },
  };

  const t = tx[lang];

  return (
    <>
      <title>Automatchlab Services - Expert Vehicle Consulting &amp; Matchmaking</title>
      <meta
        name="description"
        content="Explore Automatchlab's expert vehicle consulting services. From discovery to delivery, we match you with the right vehicle using AI-powered intelligence and human expertise."
      />
      <meta name="keywords" content="vehicle consulting, car matching service, vehicle matchmaking, expert car advice, Automatchlab services" />
      <link rel="canonical" href={`${siteUrl}/services`} />
      <meta property="og:title" content="Automatchlab Services - Expert Vehicle Consulting & Matchmaking" />
      <meta property="og:description" content="From discovery to delivery, we match you with the right vehicle using AI-powered intelligence and human expertise." />
      <meta property="og:url" content={`${siteUrl}/services`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      <div className="min-h-screen bg-white" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20 h-14 sm:h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-sm font-medium text-[#374151] hover:text-[#0F172A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-base"></i>
                </div>
                {t.back}
              </button>
              <div className="w-px h-4 bg-gray-200"></div>
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/b7d981aa05e2e3cec9e239b766e600d9/820f44dc42d7692bb1a0c21b796d9fab.svg"
                  alt="Automatchlab Logo"
                  className="h-7 sm:h-9 w-auto object-contain"
                />
              </Link>
            </div>

            <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setLang('en')}
                className={`px-2 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'en' ? 'bg-white text-[#0F172A]' : 'text-[#6B7280] hover:text-[#0F172A]'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('fr')}
                className={`px-2 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'fr' ? 'bg-white text-[#0F172A]' : 'text-[#6B7280] hover:text-[#0F172A]'}`}
              >
                FR
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Banner */}
        <div className="bg-[#0a1f16] py-8 sm:py-14 px-4 sm:px-8 lg:px-20 text-center">
          <p className="text-[#10B981] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2 sm:mb-3">Automatchlab</p>
          <h1 className="text-2xl sm:text-5xl font-bold text-white mb-2 sm:mb-4">{t.tagline}</h1>
          <p className="text-sm sm:text-base text-white/60 max-w-xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Pricing Section */}
        <PricingSection />

        {/* How It Works */}
        <HowItWorksSection />

        {/* Footer spacer */}
        <div className="h-10 sm:h-16 bg-white"></div>
      </div>
    </>
  );
}
